import type {OnboardingApplication, OnboardingApplicationSummary, OnboardingFormSchema, OnboardingOption, OnboardingStatus} from '@pocketbuy/outdoor-business-types';
import {formatDateForDisplay} from './dateFormat';
import {localFormPhone} from './formFields';

type ExportApi = Pick<typeof import('../../api').onboardingApi, 'list'|'get'|'schema'|'supervisors'|'clusters'|'banks'>;
type RecordWithSchema = {app:OnboardingApplication; summary:OnboardingApplicationSummary; schema:OnboardingFormSchema};
type Directories = {supervisors:OnboardingOption[]; clusters:OnboardingOption[]; banks:OnboardingOption[]};
const related:Record<string,string>={nextOfKin:'NEXT_OF_KIN',guarantor:'GUARANTOR',spouse:'SPOUSE',referral:'REFERRAL'};
const statuses:Record<string,string>={RETURNED:'Changes requested',PENDING_REVIEW:'Pending review',PROVISIONING:'Setup pending',PROVISION_FAILED:'Setup issue',ACTIVATED:'Activated'};
const scalar=(v:unknown):string=>v==null?'':Array.isArray(v)?v.map(scalar).join(', '):typeof v==='object'?'':String(v);
function section(app:OnboardingApplication,code:string):Record<string,unknown>{
  if(code==='personal'||code==='identity'||code==='bank'||code==='recruitment'||code==='assignment')return app[code];
  return app.relatedPeople[related[code]]??{};
}

/** Only schema-defined scalar fields are exported; never serialize entire application objects. */
export function onboardingExportTable(records:RecordWithSchema[],directories:Directories):string[][]{
  const columns=new Map<string,{section:string;field:string;label:string}>();
  for(const {schema} of records)for(const s of schema.sections){
    if(s.code==='documents')continue;
    for(const f of s.fields){
      if(['image','photo','file','document','signature','signAgreement'].includes(f.component))continue;
      const key=`${s.code}.${f.code}`;
      if(!columns.has(key))columns.set(key,{section:s.code,field:f.code,label:`${s.title} - ${f.label}`});
    }
  }
  const fields=[...columns.values()];
  const header=['Name','Country','Status',...fields.map(f=>f.label)];
  return [header,...records.map(({app,summary,schema})=>[
    app.fullName??'',app.countryCode,statuses[app.status]??app.status,
    ...fields.map(col=>{
      const s=schema.sections.find(s=>s.code===col.section);const f=s?.fields.find(f=>f.code===col.field);
      if(!f)return '';
      const values=section(app,col.section);const value=scalar(values[f.code]);if(!value)return '';
      if(f.component==='bankSelect')return scalar(values.bankName)||directories.banks.find(o=>o.code===value)?.label||'Bank name unavailable';
      if(f.component==='clusterSelect')return scalar(values.clusterName)||directories.clusters.find(o=>o.code===value)?.label||summary.clusterName||'Cluster name unavailable';
      if(f.component==='supervisorSelect')return directories.supervisors.find(o=>o.code===value)?.label||(value===String(app.ownerUserId)?summary.ownerName:undefined)||'Supervisor name unavailable';
      if(f.component==='date')return formatDateForDisplay(value,schema.dateFormat);
      if(f.component==='phone'&&schema.phoneInputFormat==='local')return localFormPhone(value,schema);
      if(f.dictionary)return schema.dictionaries[f.dictionary]?.find(o=>o.code===value&&(!f.dependsOn||o.parentCode===values[f.dependsOn]))?.label??value;
      return value;
    })
  ])];
}

export async function collectOnboardingExport(api:ExportApi,filter:{q?:string;status?:OnboardingStatus[]},checkActive:()=>void,progress:(message:string)=>void):Promise<string[][]>{
  const summaries:OnboardingApplicationSummary[]=[];const ids=new Set<number>();let expected:number|undefined;
  for(let page=0;;page++){
    checkActive();const result=await api.list({...filter,page,size:100});checkActive();
    if(expected===undefined)expected=result.total;
    if(result.total!==expected||expected>5000)throw new Error(expected>5000?'Narrow your search before exporting more than 5,000 applications.':'Applications changed during export. Please try again.');
    for(const app of result.items){if(ids.has(app.id))throw new Error('Applications changed during export. Please try again.');ids.add(app.id);summaries.push(app);}
    if(summaries.length>=expected)break;
    if(!result.items.length)throw new Error('Applications could not be fully loaded. Please try again.');
  }
  if(!summaries.length)throw new Error('No matching applications to export.');
  const [supervisors,clusters,banks]=await Promise.all([api.supervisors(),api.clusters(),api.banks()]);checkActive();
  const schemas=new Map<string,Promise<OnboardingFormSchema>>();const records:RecordWithSchema[]=[];
  for(let start=0;start<summaries.length;start+=4){
    checkActive();const group=await Promise.all(summaries.slice(start,start+4).map(async summary=>{
      const app=await api.get(summary.id);checkActive();
      if(!statuses[app.status]||app.status!==summary.status||app.updatedAt!==summary.updatedAt)throw new Error('Applications changed during export. Please try again.');
      if(!schemas.has(app.formSchemaVersion))schemas.set(app.formSchemaVersion,api.schema(app.formSchemaVersion));
      const schema=await schemas.get(app.formSchemaVersion)!;return {app,summary,schema};
    }));checkActive();records.push(...group);progress(`Preparing ${records.length} of ${summaries.length} applications…`);
  }
  return onboardingExportTable(records,{supervisors,clusters,banks});
}

export async function onboardingExcel(table:string[][]){
  const {default:ExcelJS}=await import('exceljs');
  const workbook=new ExcelJS.Workbook();const sheet=workbook.addWorksheet('Agents');
  sheet.addRows(table);sheet.views=[{state:'frozen',ySplit:1,xSplit:1}];
  sheet.autoFilter={from:{row:1,column:1},to:{row:table.length,column:table[0].length}};
  sheet.columns.forEach((column,index)=>{column.width=index===0?30:26;column.numFmt='@';});
  sheet.eachRow(row=>{row.alignment={vertical:'top',wrapText:true};});
  sheet.getRow(1).height=42;sheet.getRow(1).font={bold:true,color:{argb:'FFFFFFFF'}};
  sheet.getRow(1).fill={type:'pattern',pattern:'solid',fgColor:{argb:'FF193A30'}};
  // Strings stay literal, including values beginning with '='; never create formulas or hyperlinks.
  const buffer=await workbook.xlsx.writeBuffer();
  return new Blob([new Uint8Array(buffer)],{type:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
}
